/**
 * 1.5.17 §4b — the page behind a sheet stays where it is. An iPhone running the app from the home screen
 * ignores `overflow:hidden` on the body (which is all Radix's own lock sets), so the page slid about under the
 * Ask sheet. This is the fixed-body lock: remember how far the page was scrolled, pin the body there, and on
 * release put the body's own styles back and scroll to the same place. It touches only these five inline
 * styles, so it sits beside Radix's lock (an attribute and a stylesheet) without either undoing the other.
 *
 * 1.5.19 §4 — the release. The page is put back **once**, and only when it can stay put: after Radix's own lock
 * (`body[data-scroll-locked]`) has gone and, on a phone, after the keyboard has gone down (a `scrollTo` under a
 * keyboard that is still closing is moved again by iOS). Until then the body stays pinned — a few frames, half a
 * second at most. That wait is counted in animation frames, never hung on a viewport event (the rule: a scroll
 * or viewport event handler never scrolls anything). A sheet opened again while a release is waiting finishes
 * that release first, so it never pins the page at a place the page was not.
 */
import { useEffect } from 'react';
import { KEYBOARD_MIN, readVisualViewport } from './useVisualViewport';

const PROPS = ['position', 'top', 'left', 'right', 'width'] as const;
/** The longest the release waits for the keyboard and Radix's lock: about half a second. */
const MAX_FRAMES = 30;

/** The release that is waiting for the keyboard to go, if any (there is one body, so there is one). */
let waiting: (() => void) | null = null;

/** A sheet has the body pinned: nothing should be scrolled into view behind it. */
export const pagePinned = (): boolean => typeof document !== 'undefined' && document.body.style.position === 'fixed';

const settled = (): boolean => (readVisualViewport()?.inset ?? 0) < KEYBOARD_MIN && !document.body.hasAttribute('data-scroll-locked');

export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    waiting?.();
    // 1.8.4 §7: every sheet pins the page (src/components/ui/sheet.tsx) — a sheet inside a sheet, or the Ask sheet's own
    // lock beside its sheet's, finds the page already pinned and leaves it to the lock that pinned it
    if (pagePinned()) return;
    const body = document.body;
    const y = window.scrollY;
    const before = PROPS.map((p) => [p, body.style.getPropertyValue(p), body.style.getPropertyPriority(p)] as const);
    // Radix's own lock writes `body[data-scroll-locked]{position:relative !important}` in a stylesheet: only an
    // inline `!important` wins over it, and without that the body is never pinned at all.
    body.style.setProperty('position', 'fixed', 'important');
    body.style.setProperty('top', `${-y}px`, 'important');
    body.style.setProperty('left', '0', 'important');
    body.style.setProperty('right', '0', 'important');
    body.style.setProperty('width', '100%', 'important');
    return () => {
      let frame = 0;
      let frames = 0;
      const release = () => {
        if (waiting !== release) return;
        waiting = null;
        cancelAnimationFrame(frame);
        for (const [p, value, priority] of before) {
          if (value) body.style.setProperty(p, value, priority);
          else body.style.removeProperty(p);
        }
        window.scrollTo(0, y);
      };
      const look = () => {
        if (settled() || ++frames >= MAX_FRAMES) release();
        else frame = requestAnimationFrame(look);
      };
      waiting = release;
      look();
    };
  }, [active]);
}
